<template>
    <div class="content-wrapper pt-6 px-8">
        <el-form>
            <div class="flex items-center mb-2">
                <BaseRadioGroup
                    v-model="activeTab"
                    :radios="radioTabsData"
                    class="radio-groups"
                />
                <div class="mx-4">
                    <div v-if="activeTab === 'user'" class="relative">
                        <div class="flex">
                            <base-select
                                v-model="user"
                                labelKey="text"
                                valueKey="value"
                                :multiple="true"
                                :data="allUsers"
                                :placeholder="$t('general.user')"
                                showTooltip
                            >
                            </base-select>
                            <base-button
                                size="sml"
                                type="primary"
                                outline
                                @click="saveData('user')"
                                class="mx-4"
                                :disabled="disableUserBtn"
                            >
                                <span class="mx-0-5">{{ $t('general.add') }}</span>
                            </base-button>
                        </div>
                        <div class="el-form-item__error" v-show="recipientError.user">
                            {{ $t('recipient.error.alreadyExist') }}
                        </div>
                    </div>
                    <div v-else-if="activeTab === 'account'" class="relative">
                        <div class="flex">
                            <base-select
                                v-model="account"
                                labelKey="text"
                                valueKey="value"
                                :multiple="true"
                                :data="allAccounts"
                                :placeholder="$t('general.account')"
                                showTooltip
                            >
                            </base-select>
                            <base-button
                                size="sml"
                                type="primary"
                                outline
                                @click="saveData('account')"
                                class="mx-4"
                                :disabled="disableAccountBtn"
                            >
                                <span class="mx-0-5">{{ $t('general.add') }}</span>
                            </base-button>
                        </div>
                        <div class="el-form-item__error" v-show="recipientError.account">
                            {{ $t('recipient.error.alreadyExist') }}
                        </div>
                    </div>
                    <div v-else class="relative">
                        <div class="flex">
                            <el-input
                                :placeholder="$t('general.email')"
                                v-model="email"
                                type="email"
                                clearable>
                            </el-input>
                            <base-button
                                size="sml"
                                type="primary"
                                outline
                                @click="saveData('email')"
                                class="mx-4"
                                :disabled="disableEmailBtn"
                            >
                                <div class="flex items center">
                                    <i class="vc-icon-add icon-md" :class="$rtl.isRTL ? 'ml-2' : 'mr-2'" />
                                    {{ $t('general.add') }}
                                </div>
                            </base-button>
                        </div>
                        <div class="el-form-item__error" v-show="recipientError.email">
                            {{ recipientErrorText }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="relative flex items-center mb-2 main-text-color">
                <span>
                    {{ $t('report.recipients') }}
                </span>
                <span class="mx-2 text-red text-xs" v-show="isRecipientsListIsEmpty">
                    ({{ $t('validation.error.fieldIsRequired') }})
                </span>
            </div>
            <div class="h-8">
                <delimited-list :list="recipients" :limit="10" separator=",">
                    <template v-slot:list-item="{item}">
                        <rec-item
                            v-if="item.type === 'email'"
                            :item="item"
                            :name="get(item, 'text', '- -')"
                            show-delete-button
                            @delete="onDelete"
                        />
                        <span v-else>
                            <el-tooltip placement="top" :open-delay="300">
                                <div slot="content">{{ get(item, 'email', '- -') }}</div>
                                <div>
                                    <rec-item
                                        :item="item"
                                        :name="get(item, 'text', '- -')"
                                        show-delete-button
                                        @delete="onDelete"
                                    />
                                </div>
                            </el-tooltip>
                        </span>
                    </template>
                    <template v-slot:other-icon="{data}">
                        <tag content-class="icon-ellipsis">+ {{ data }}</tag>
                    </template>
                    <template v-slot:other-content="{list}">
                        <div class="max-w-lg">
                            <div
                                v-for="(item, index) in list" :key="index"
                                class="flex-1 items-center justify-start p-1"
                            >
                                <tag v-if="item.type === 'email'">
                                    {{ get(item, 'text', '- -') }} <i class="mx-1 vc-icon-close text-xs cursor-pointer" @click="onDelete(item)" />
                                </tag>
                                <span v-else>
                                    <el-tooltip placement="right" :open-delay="300">
                                        <div slot="content">{{ get(item, 'email', '- -') }}</div>
                                        <div class="flex">
                                            <tag>
                                                {{ get(item, 'text', '- -') }} <i class="mx-1 vc-icon-close text-xs cursor-pointer" @click="onDelete(item)" />
                                            </tag>
                                        </div>
                                    </el-tooltip>
                                </span>
                            </div>
                        </div>
                    </template>
                </delimited-list>
            </div>
            <el-form-item class="mb-1">
                <label class="relative flex items-center">
                    <span class="main-text-color">
                        {{ $t('report.subject') }}
                    </span>
                    <span class="custom-el-form-item__error" v-show="emailError.subject">
                        ({{ $t('validation.error.fieldIsRequired') }})
                    </span>
                </label>
                <html-editor
                    :has-buttons="hasButtons"
                    ref="editor"
                    v-model="model.subject"
                    class="subject-editor"
                    :config="{
                        plugins: ['variable'],
                        focus: false
                    }"
                    :availableVariables="reportTemplateVariableList"
                    :buttonsHide="['html', 'format', 'bold', 'italic', 'lists', 'link', 'Strikethrough', 'imagemanager', 'image', 'deleted']"
                />
            </el-form-item>
            <el-form-item>
                <label class="relative flex items-center">
                    <span class="main-text-color">
                        {{ $t('report.editor.text') }}
                    </span>
                    <span class="custom-el-form-item__error" v-show="emailError.text">
                        ({{ $t('validation.error.fieldIsRequired') }})
                    </span>
                </label>
                <html-editor
                    :has-buttons="hasButtons"
                    ref="editor"
                    v-model="model.text"
                    :config="{
                        plugins: ['variable'],
                        minHeight: '100px',
                        focus: false
                    }"
                    minHeight="250px"
                    :availableVariables="reportTemplateVariableList"
                    :buttonsHide="['html', 'format', 'bold', 'italic', 'lists', 'link', 'Strikethrough', 'imagemanager', 'image', 'deleted']"
                />
            </el-form-item>
        </el-form>
        <portal to="next-button">
            <div class="flex">
                <div
                    @click="onBack"
                    class="text-primary-300 flex items-center hover:text-primary cursor-pointer mx-16"
                >
                    <IconDirLeft v-if="!$rtl.isRTL" class="mx-1" />
                    <IconDirRight v-else class="mx-1" />
                    <span class="mx-1">{{ $t('general.back') }}</span>
                </div>
                <base-button
                    fixed-width="w-37"
                    size="md"
                    type="primary"
                    @click="onFinish"
                >
                    <div class="flex items-center">
                        <IconSave class="mx-1"/>
                        <span class="mx-1 text-base font-bold">
                            {{ $t('general.finish') }}
                        </span>
                    </div>
                </base-button>
            </div>
        </portal>
    </div>
</template>
<script>
    import { Select, Option, Tooltip } from 'element-ui'
    import { validateEmail } from '@/helpers/util'
    import { reportTriggerApi } from "@/modules/reports/services/reportTriggerService"
    import cloneDeep from 'lodash/cloneDeep'
    import DelimitedList from "@/modules/reports/components/DelimitedList"
    import RecItem from "@/modules/reports/components/RecItem"
    import Tag from "@/modules/reports/components/Tag"

    const ReportRecipientTypeIDS = {
        User: 1,
        Account: 2,
        Email: 3
    }
    const keys = ['account', 'user']

    export default {
        props: {
            report: {
                type: Object,
                default: () => ({}),
            },
            create: {
                type: Boolean,
                default: false
            },
            scheduleData: {
                type: Object,
                default: () => ({})
            },
            dataToEdit: {
                type: Object,
                default: () => ({})
            }
        },
        components: {
            HtmlEditor: () => import('@/components/Html/HtmlEditor'),
            [Select.name]: Select,
            [Option.name]: Option,
            BaseButton: () => import("@/components/Common/Buttons/BaseButton"),
            DelimitedList,
            RecItem,
            Tag,
            [Tooltip.name]: Tooltip
        },
        data() {
            return {
                hasButtons: false,
                recipients: [],
                model: {
                    subject: '',
                    text: ''
                },
                isRecipientsListIsEmpty: false,
                radioTabsData: [
                    { label: this.$t('general.user'), value: 'user' },
                    { label: this.$t('general.account'), value: 'account' },
                    { label: this.$t('general.email'), value: 'email' }
                ],
                account: [],
                user: [],
                email: '',
                activeTab: 'email',
                recipientError: {
                    user: false,
                    account: false,
                    email: false
                },
                recipientErrorText: '',
                emailError: {
                    text: false,
                    subject: false
                }
            }
        },
        computed: {
            reportTemplateVariableList() {
                return this.$store.getters['reportTrigger/getConfData'].ReportTemplateVariableList
                    .map(el => {
                        return {
                            text: this.$t(el.ReportTemplateVariableNameTag),
                            value: el.ReportTemplateVariableTag
                        }
                    })
            },
            allAccounts () {
                return this.$store.getters['entities/getEntityList']('Accounts')
                    .filter(el => 'AccountEmail' in el)
                    .map(el => {
                        return {
                            text: el.dist_name,
                            value: el.AccountID,
                            type: 'account',
                            email: el.AccountEmail
                        }
                    })
                    .sort((a, b) => a.text !== b.text ? a.text < b.text ? -1 : 1 : 0);
            },
            allUsers () {
                return this.$store.getters['entities/getEntityList']('Users')
                    .filter(el => 'UserEmail' in el)
                    .map(el => {
                        return {
                            text: el.user_name,
                            value: el.user_id,
                            type: 'user',
                            email: el.UserEmail
                        }
                    })
                    .sort((a, b) => a.text !== b.text ? a.text < b.text ? -1 : 1 : 0);
            },
            reportRecipients () {
                const allAccounts = this.allAccounts
                const allUsers = this.allUsers

                return allAccounts.concat(allUsers)
            },
            recipientsLength () {
                return this.recipients.length
            },
            disableUserBtn () {
                return this.user.length === 0
            },
            disableAccountBtn () {
                return this.account.length === 0
            },
            disableEmailBtn () {
                return this.email === ''
            }
        },
        watch: {
            recipientsLength (val) {
                this.isRecipientsListIsEmpty = !val
            },
            account () {
                this.recipientError.account = false
            },
            user () {
                this.recipientError.user = false
            },
            email () {
                this.recipientError.email = false
            }
        },
        methods: {
            async onFinish() {
                this.isRecipientsListIsEmpty = !this.recipientsLength
                this.emailError.text = !this.model.text
                this.emailError.subject = !this.model.subject
                if (!this.recipientsLength || !this.model.subject || !this.model.text) {

                    return
                }
                await this.updateReportTriggerRecipientsData()
                const reportTriggerData = await this.updateReportTriggerData()

                if (this.create) {
                    if (!this.dataToEdit || !Object.keys(this.dataToEdit).length) {
                        this.$emit('pushDataInReportTrigger', reportTriggerData)
                    } else {
                        const data = {
                            indexToEdit: this.dataToEdit.indexToEdit,
                            value: reportTriggerData
                        }
                        this.$emit('updateReportTriggerItem', data)
                    }
                    this.$emit('on-finish')
                    return
                }

                if (this.dataToEdit && Object.keys(this.dataToEdit).length) {
                    const data = {
                        indexToEdit: this.dataToEdit.indexToEdit,
                        value: reportTriggerData
                    }
                    this.$emit('updateReportTriggerItem', data)
                    await reportTriggerApi.createReportTrigger(reportTriggerData)
                    this.$emit('on-finish')
                    return
                }

                try {
                    const { ReportTiggerID } = await reportTriggerApi.createReportTrigger(reportTriggerData)
                    reportTriggerData.ReportTriggerID = ReportTiggerID
                    this.$emit('pushDataInReportTrigger', reportTriggerData)
                    this.$emit('on-finish')
                } catch {

                }
            },
            tryAddEmail(values) {
                const targetItem = values[values.length - 1]
                if (validateEmail(targetItem) || (/^-?\d+$/.test(targetItem) && this.reportRecipients.some(el => Number(el.value) === Number(targetItem)))) {
                    return
                }

                if (!validateEmail(targetItem) || /^-?\d+$/.test(targetItem)) {
                    const indexToRemove = this.recipients.indexOf(targetItem)
                    if (indexToRemove !== -1) {
                        this.recipients.splice(indexToRemove, 1)
                    }
                }
            },
            async onBack () {
                await this.updateReportTriggerRecipientsData()

                this.$emit('on-back')
            },
            searchReportRecipientTypeIDById (id) {
                const user = this.allUsers.find(el => Number(el.value) === Number(id))
                const account = this.allAccounts.find(el => Number(el.value) === Number(id))

                if (user) {
                    return {
                        ...user,
                        typeId: ReportRecipientTypeIDS.User
                    }
                }
                if (account) {
                    return  {
                        ...account,
                        typeId: ReportRecipientTypeIDS.Account
                    }
                }
                return
            },
            async updateReportTriggerRecipientsData () {
                const recipients = this.recipients.map(el => {
                    let newObject = {}
                    if (el.type !== 'email') {
                        newObject.RecipientID = el.value,
                        newObject.Email = el.email
                        newObject.ReportRecipientTypeID = el.typeId
                    } else {
                        newObject.Email = el.value
                        newObject.ReportRecipientTypeID = el.typeId
                    }

                    return newObject
                })

                let subject = this.model.subject
                let text = this.model.text
                this.reportTemplateVariableList.forEach(el => {
                    subject = subject.replaceAll(`<span data-redactor-type="variable">${el.text}</span>`, el.value)
                    text = text.replaceAll(`<span data-redactor-type="variable">${el.text}</span>`, el.value)
                })

                const data = {
                    EmailSubject: subject,
                    EmailBody: text,
                    ReportRecipient: recipients
                }

                this.$emit('updateSchedule', data, ['EmailSubject', 'EmailBody', 'ReportRecipient'])
            },
            async updateReportTriggerData () {
                const data = cloneDeep(this.scheduleData)

                if (data.ReportTriggerCondition.length) {
                    data.ReportTriggerCondition = data.ReportTriggerCondition
                        .map(condition => {
                            const filteredReportTriggerConditionFilter = condition.ReportTriggerConditionFilter
                                .filter(el => el.ConditionFilterColumnTypeID && el.ConditionFilterOperatorID && (el.ConditionFilterValue !== undefined && el.ConditionFilterValue !== null) && el.WidgetTemplateColumnID)

                            return filteredReportTriggerConditionFilter.length ? condition : {}
                        })
                } else {
                    data.ReportTriggerCondition = [{}]
                }

                if ('TriggerTimeRange' in data.ScheduleData) {
                    const timeRange = data.ScheduleData.TriggerTimeRange
                    data.ScheduleData.TriggerTimeRange = {
                        Start: timeRange[0],
                        End: timeRange[1]
                    }
                }
                if ('TriggerDayOfWeek' in data.ScheduleData) {
                    data.ScheduleData.TriggerDayOfWeek.sort((a, b) => a - b)
                }
                return data
            },
            saveData (key) {
                if (keys.includes(key)) {
                    this[key].forEach(el => {
                        const itemById = this.searchReportRecipientTypeIDById(el)
                        if (!this.recipients.some(el => el.value === itemById.value)) {
                            this.recipients.push(itemById)
                            this[key] = []
                        } else {
                            this.recipientError[key] = true
                        }
                    })
                } else {
                    this.recipientErrorText = ''
                    if (!validateEmail(this[key])) {
                        this.recipientError[key] = true
                        this.recipientErrorText = this.$t('recipient.error.notValidEmail')
                        return
                    }
                    if (!this.recipients.some(el => el.value === this[key]) && validateEmail(this[key])) {
                        this.recipients.push({
                            text: this[key],
                            type: 'email',
                            value: this[key],
                            typeId: ReportRecipientTypeIDS.Email
                        })
                        this[key] = ''
                    } else {
                        this.recipientError[key] = true
                        this.recipientErrorText = this.$t('recipient.error.alreadyExist')
                    }
                }
            },
            onDelete (item) {
                const indexToDelete = this.recipients.findIndex(el => el.value === item.value)
                this.recipients.splice(indexToDelete, 1)
            }
        },
        mounted () {
            if (this.scheduleData.ReportRecipient) {
                const findTheSameRecipient = (key, id) => {
                    let newItem = {}
                    if (key === 'account') {
                        newItem = this.allAccounts.find(el => Number(el.value) === Number(id))
                    } else {
                        newItem = this.allUsers.find(el => Number(el.value) === Number(id))
                    }
                    return newItem.text
                }

                this.recipients = this.scheduleData.ReportRecipient.map(el => {
                    let type = ''
                    let text = ''

                    Object.entries(ReportRecipientTypeIDS).forEach(item => {
                        if (item[1] === el.ReportRecipientTypeID) {
                            type = item[0].toLowerCase()
                        }
                    })
                    if (keys.includes(type)) {
                        text = findTheSameRecipient(type, el.RecipientID)
                    } else {
                        text = el.Email
                    }

                    return {
                        text: text,
                        type: type,
                        value: type === 'email' ? el.Email : el.RecipientID,
                        typeId: el.ReportRecipientTypeID,
                        email: el.Email || ''
                    }
                })
            }

            this.model.subject = this.scheduleData.EmailSubject ? this.scheduleData.EmailSubject : this.$t('report.trigger.email.subject')
            this.model.text = this.scheduleData.EmailBody ? this.scheduleData.EmailBody : this.$t('report.trigger.email.body')
        }
    }
</script>

<style lang="scss" scoped>
[dir="rtl"] .el-form {
    @apply text-right;
}
[dir="ltr"] .el-form {
    @apply text-left;
}
::v-deep .el-tabs__nav-scroll {
    display: flex;
}
::v-deep .el-select, ::v-deep .el-input {
    width: 300px;
}
::v-deep .el-tab-pane {
    position: relative;
}
.subject-editor ::v-deep.redactor-styles {
    padding: 0px;
}
::v-deep .vc-radio-group-wrapper {
    width: inherit !important;
}
::v-deep .radio-groups {
    @apply flex items-center mt-2;
}
::v-deep .subject-editor .redactor-styles p {
    margin-bottom: 0px !important;
}
.custom-el-form-item__error {
    color: #F56C6C;
    font-size: 12px;
    line-height: 1;
    @apply px-2;
}
</style>
